import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Stickyfill from 'stickyfill';
import get from 'lodash.get';

import { BLOG_PROP_TYPES, BLOG_DEFAULT_PROPS } from 'lib/CustomPropTypes/blog';
import { fetchLatestCardsByTaxonomy, updateActiveCards } from 'redux/modules/liveBlog';
import { getHourDifference } from 'lib/DateTime';
import { isSingleCardRoute } from 'lib/liveBlog';
import { listenForHFSBump } from 'lib/listenForHFSBump';
import { LiveBlogAdController } from 'lib/AdControllers';
import { loadMoreCards, blogMapStateToProps } from 'lib/blogUtils';
import { parseOnClient } from 'lib/urlUtils';
import amp from 'lib/amp';
import scrollToElem from 'lib/scrollToElement';

import Ad from 'components/Ad';
import AMP from 'components/LiveBlog/AMP';
import Button from 'components/LiveBlog/Button';
import Card from 'components/Card';
import Pill from 'components/LiveBlog/Pill';
import styles from 'components/LiveBlog/styles.module.scss';
import WindowTopObserver from 'components/IntObserver/WindowTopObserver';

const queryLimit = 11;

const {
  LIVE_BLOG_POLLING_INTERVAL = 30000,
} = process.env;

const mapStateToProps = blogMapStateToProps;

const headerID = 'blogHeader';

@connect(mapStateToProps)
@amp(() => <AMP isRegularBlog />)
class Blog extends React.Component {
  static propTypes = BLOG_PROP_TYPES;

  static contextTypes = {
    store: PropTypes.objectOf(PropTypes.any),
  };

  static defaultProps = BLOG_DEFAULT_PROPS;

  constructor(props) {
    super(props);

    this.headerRef = React.createRef();
    this.scrollUpButtonRef = React.createRef();
    this.pillRef = React.createRef();

    this.state = {
      isScrollingUp: false,
      isScrollingToTop: false,
      isScrolledPastFirstCard: false,
      shouldUpdateCards: false,
      shouldHideNewPostsButton: false,
    };
  }

  componentDidMount() {
    const { isCanonicalCard } = this.props;

    // Query new cards
    this.startPolling();
    listenForHFSBump(this, 'isScrollingUp');

    this.stickyfill = Stickyfill();

    if (this.scrollUpButtonRef.current) {
      this.pillRef.addEventListener('transitionend', this.onTransitionEnd);

      this.stickyfill.add(this.scrollUpButtonRef.current);
      this.stickyfill.add(this.pillRef);
    }

    if (isCanonicalCard && window && window.history) {
      const [baseUrl] = window.location.href.split('#');
      const basePath = parseOnClient(baseUrl).pathname;
      window.history.replaceState(null, null, `${basePath || baseUrl}#${headerID}`);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillUpdate({ fetchingLatest: isFetching }) {
    const { fetchingLatest: wasFetching } = this.props;

    // Check status of polling when latest fetch completes
    if (wasFetching && !isFetching && !this.firstCardLessThan12HoursOld()) {
      clearInterval(this.cardPollInterval);
    }
  }

  componentWillUnmount() {
    clearInterval(this.cardPollInterval);

    if (this.stickyfill && this.scrollUpButtonRef.current && this.pillRef) {
      this.pillRef.removeEventListener('transitionend', this.onTransitionEnd);

      this.stickyfill.remove(this.scrollUpButtonRef.current);
      this.stickyfill.remove(this.pillRef);
    }
  }

  onTransitionEnd = (e) => {
    if (e.propertyName === 'opacity') {
      this.updateActiveCards();
    }
  }

  updateActiveCards = () => {
    const { shouldUpdateCards } = this.state;
    const { dispatch } = this.props;

    if (shouldUpdateCards) {
      dispatch(updateActiveCards());
      this.setState({ shouldUpdateCards: false, shouldHideNewPostsButton: false });
    }
  }

  startPolling = () => {
    const { path } = this.props;
    // Initial page load poll of new cards
    if (isSingleCardRoute(path)) {
      this.queryCards();
    }
    // Set up new card polling
    if (this.firstCardLessThan12HoursOld() || isSingleCardRoute(path)) {
      // Interval cleared in componentWillUpdate and componentWillUnmount
      this.cardPollInterval = setInterval(this.queryCards, LIVE_BLOG_POLLING_INTERVAL);
    }
  };

  scrollToTop = (cb = () => { }) => {
    const { isScrollingToTop } = this.state;

    if (
      typeof window === 'undefined'
      || isScrollingToTop
      || (!this.headerRef.current || !this.headerRef.current.getBoundingClientRect)
    ) {
      return null;
    }

    this.setState({ isScrollingToTop: true });

    const absoluteScrollPos = this.headerRef.current.getBoundingClientRect().top
      + window.pageYOffset
      - 170;

    return scrollToElem(absoluteScrollPos, 1000, 'easeInOut', () => {
      const { shouldUpdateCards } = this.state;
      this.setState({
        isScrollingToTop: false,
        shouldHideNewPostsButton: shouldUpdateCards,
      });

      if (typeof cb === 'function') cb();
    });
  };

  firstCardInViewListener = ([intObsrvEntry]) => {
    const { isScrolledPastFirstCard } = this.state;

    if (intObsrvEntry.intersectionRatio >= 0.5 && !isScrolledPastFirstCard) {
      this.setState({ isScrolledPastFirstCard: true });
    } else if (isScrolledPastFirstCard) {
      this.setState({ isScrolledPastFirstCard: false });
    }
  }

  firstCardLessThan12HoursOld = () => {
    const publishedAt = get(this.props, 'activeItems[0].date.publishedAt', '');
    return getHourDifference(new Date(publishedAt), Date.now()) < 12;
  }

  queryCards = (page = 1) => {
    const { dispatch, content: { taxonomy: { path } }, fetchingLatest } = this.props;

    if (!fetchingLatest) {
      dispatch(fetchLatestCardsByTaxonomy({ path, queryLimit, page }));
    }
  }

  scrollToTopAndUpdateCards = () => {
    this.setState({ shouldUpdateCards: true });
    this.scrollToTop();
  }

  isLastCardPageRendered = () => {
    const { trueTotalItems, activeItems } = this.props;
    return trueTotalItems === activeItems.length;
  };

  renderBackToTopButton = () => {
    const { isScrollingUp, isScrollingToTop, isScrolledPastFirstCard } = this.state;
    const { numberOfNewItems } = this.props;

    return (
      <button
        type="button"
        className={classNames(
          'z-999 bg-secondary bl-0 br-0 bt-0 bb-0 df justify-center items-center pa0 overflow-y-hidden',
          styles.button,
          styles.backToTop,
          {
            [styles.scrollingUp]: isScrollingUp,
            [styles.hidden]: numberOfNewItems || (!isScrolledPastFirstCard && !isScrollingToTop),
          },
        )}
        onClick={this.scrollToTop}
        ref={this.scrollUpButtonRef}
        title="Back to Top"
      >
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 29.75V9.91667" stroke="white" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" />
          <path d="M7.08317 18.417L16.9998 8.50033L26.9165 18.417" stroke="white" strokeWidth="2" strokeLinecap="square" />
          <rect x="2.8335" y="2.83301" width="28.3333" height="2.41667" fill="white" />
        </svg>
      </button>
    );
  }

  renderScrollUpButton = () => {
    const { isScrollingUp, shouldHideNewPostsButton } = this.state;
    const { numberOfNewItems } = this.props;

    return (
      <div
        className={classNames(
          'absolute left0 right0 bottom0',
          styles.scrollUpButtonContainer,
        )}
      >
        <Pill
          count={numberOfNewItems}
          onClick={this.scrollToTopAndUpdateCards}
          className={classNames(
            'z-999 overflow-y-hidden',
            styles.button,
            styles.newPostsButton,
            {
              [styles.scrollingUp]: isScrollingUp,
              [styles.hidden]: !numberOfNewItems || shouldHideNewPostsButton,
            },
          )}
          onRef={(ref) => {
            this.pillRef = ref;
          }}
        />
        {this.renderBackToTopButton()}
      </div>
    );
  };

  renderCards = () => {
    const {
      path,
      domain,
      activeItems,
      trueTotalItems,
    } = this.props;
    const cardsWithAds = LiveBlogAdController.insertBoxinlineAds(activeItems);
    const regexForCardID = /(?:\/ncrd)[^]*$/g;
    const canonicalUrl = `https://www.${domain + path}`.replace(regexForCardID, '');
    let cardCount = 0;

    return cardsWithAds.map((card, i) => {
      if (card.type === 'ad') {
        return <Ad key={card.id} {...card} adClass="mb8" />;
      }

      // Store count of rendered cards
      cardCount += 1;
      // Rendered cards equals true count of cards
      const isLastCard = cardCount === trueTotalItems;

      const cardComponent = (
        <Card
          {...card}
          isLastCard={isLastCard}
          path={path}
          key={card.id}
          canonicalUrl={canonicalUrl}
        />
      );

      return i === 0
        ? (
          <WindowTopObserver
            key={card.id}
            threshold={0.5}
            callback={this.firstCardInViewListener}
          >
            {cardComponent}
          </WindowTopObserver>
        )
        : cardComponent;
    });
  }

  renderLoadMoreButton = () => {
    const { loadingMoreCards } = this.props;
    return (
      <Button onClick={() => loadMoreCards(this.props)} loadingMoreCards={loadingMoreCards} />
    );
  }

  render() {
    const { activeItems } = this.props;

    return (
      <React.Fragment>
        <div id={headerID} className="Blog relative mt8" ref={this.headerRef}>
          {!!activeItems.length
            && this.renderScrollUpButton()}
          <div className="cardsContainer">
            {this.renderCards()}
          </div>
          {!this.isLastCardPageRendered() && this.renderLoadMoreButton()}
        </div>
      </React.Fragment>
    );
  }
}

export default Blog;
